
(() => {
  "use strict";

  if (window.__TDOG_THEME_CUSTOMIZER_LOADED__) {
    return;
  }
  window.__TDOG_THEME_CUSTOMIZER_LOADED__ = true;

  /************************************************************
   * Configuration
   ************************************************************/
  const CONFIG = Object.freeze({
    styleId: "tdog-style-injected",
    logPrefix: "[TDog Script]",
    debugStorageKey: "tdogThemeDebug",
    debugQueryParam: "tdogDebug",
    globalObserverDebounceMs: 75,
    rapiSyncDebounceMs: 60,
    engraveSyncDebounceMs: 60,
    waitForRapiTimeoutMs: 10000,
    waitForRapiPollMs: 250
  });

  const SELECTORS = Object.freeze({
    personaliseButton: "div.cl-po--swatch.cl-po--swatch-button.cl-po--small",
    productCard: ".product-card",
    productCardMedia: ".product-card__media",
    newBadgeConfig: 'script[type="application/json"][id^="new-badge-config-"]',
    rapiRoot: ".rapi-bundles, [class*='rapi-bundle-content']",
    productFormWrapper: ".product-form-wrapper",
    productForm: 'form[is="product-form"], form[action="/cart/add"]',
    buyButtonByFormId: (formId) => `button.product-form__submit[form="${cssEscape(formId)}"]`,
    buyPriceContainerByFormId: (formId) => `#BuyButtonPrice-${cssEscape(formId)}`,
    buyButtonLabel: ".btn-text > span:first-child",
    checkedBundleRadio: ".rapi-bundles__bar input[type='radio']:checked",
    bundleVariantSelect: ".rapi-bundles__bar-variant-select",
    bundleTitle: ".rapi-bundles__bar-title",
    bundlePrice: ".rapi-bundles__bar-price",
    engraveWrapper: ".cl-po--wrapper",
    engraveGroup: '.cl-po--option[data-option][data-option$="Group"]',
    engraveCollapse: ".cl-po--collapse",
    engravePanel: ".cl-po--panel",
    engraveTextInput: 'input.cl-po--input[type="text"], textarea.cl-po--input, input[type="text"], textarea',
    engraveFontSelected: '.cl-po--option[data-option$="Font"] input[type="radio"]:checked',
    engraveApproveCheckbox: '.cl-po--option[data-option^="Approve"] input[type="checkbox"]'
  });

  /************************************************************
   * Debug logging
   ************************************************************/
  function isDebugEnabled() {
    try {
      const query = new URLSearchParams(window.location.search);
      const queryValue = query.get(CONFIG.debugQueryParam);
      if (queryValue === "1" || queryValue === "true") return true;
      if (queryValue === "0" || queryValue === "false") return false;

      const storageValue = window.localStorage?.getItem(CONFIG.debugStorageKey);
      if (storageValue === "1" || storageValue === "true") return true;
      if (storageValue === "0" || storageValue === "false") return false;

      return document.documentElement?.dataset?.tdogDebug === "1";
    } catch {
      return false;
    }
  }

  const DEBUG = isDebugEnabled();

  function log(...args) {
    if (!DEBUG) return;
    console.log(CONFIG.logPrefix, ...args);
  }

  function warn(...args) {
    if (!DEBUG) return;
    console.warn(CONFIG.logPrefix, ...args);
  }

  function error(...args) {
    console.error(CONFIG.logPrefix, ...args);
  }

  function safeRun(name, fn, fallbackValue) {
    try {
      return fn();
    } catch (err) {
      warn(`${name} failed`, err);
      return fallbackValue;
    }
  }

  /************************************************************
   * CSS
   ************************************************************/
  const CSS = `
    .tdog-personalise{border-radius:9999px;padding:16px 28px;min-height:56px;font-size:16px;font-weight:600;line-height:1.4;color:#fff;display:inline-flex;align-items:center;justify-content:center;background-color:#de6ac1;box-shadow:0 1px 3px rgba(0,0,0,.2);border:none;width:100%;transition:background-color .2s ease,box-shadow .2s ease;cursor:pointer;text-align:center;margin:0 auto}
    .tdog-personalise:hover{background-color:#d24db7;box-shadow:0 4px 6px rgba(0,0,0,.3)}
    .tdog-personalise:active{background-color:#c043a8;box-shadow:inset 0 2px 4px rgba(0,0,0,.3)}
    .badge--new{background:#16a34a;color:#fff;font-weight:600;padding:.375rem .625rem;border-radius:9999px;line-height:1;display:inline-flex;align-items:center;white-space:nowrap;pointer-events:none;font-size:.875rem}
    .product-card__media .badge--new{position:absolute;top:.5rem;left:50%;transform:translateX(-50%);z-index:3}
    @media (max-width:768px){.product-card__media .badge--new{top:.625rem}}

    /* ===== Engraving UX v3: minimal + Concept-friendly ===== */
    :root{
      --tdog-neutral: rgba(0,0,0,.55);
      --tdog-muted: rgba(0,0,0,.12);
      --tdog-bg: rgba(0,0,0,.03);

      --tdog-warn: #F98027;
      --tdog-good: #05ADAE;
      --tdog-info: #F98027;
    }

    .tdog-engrave-progress{
      margin: 14px 0 12px;
      padding: 12px 14px;
      border-radius: 12px;
      border: 1px solid var(--tdog-muted);
      background: var(--tdog-bg);
    }
    .tdog-engrave-progress__top{
      display:flex;
      justify-content:space-between;
      gap:10px;
      align-items:baseline;
      margin-bottom:8px;
    }
    .tdog-engrave-progress__title{ font-weight: 800; font-size: 14px; }
    .tdog-engrave-progress__count{ font-weight: 700; font-size: 13px; color: var(--tdog-neutral); white-space:nowrap; }
    .tdog-engrave-progress__bar{
      height: 10px;
      border-radius: 9999px;
      overflow:hidden;
      background: rgba(0,0,0,.10);
      position: relative !important;
    }
    .tdog-engrave-progress__fill{
      height:100%;
      width:0%;
      border-radius:9999px;
      background: var(--tdog-info);
      display: block !important;
      flex: none !important;
      max-width: none !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
      transition: width .35s ease, background-color .25s ease;
    }
    .tdog-engrave-progress__hint{
      margin-top: 8px;
      font-size: 12.5px;
      color: rgba(0,0,0,.62);
      line-height: 1.35;
    }

    .tdog-engrave-progress.is-not-started .tdog-engrave-progress__fill{
      background:#d1d5db;
    }

    .tdog-engrave-progress.is-in-progress .tdog-engrave-progress__fill{
      background:var(--tdog-warn);
    }

    .tdog-engrave-progress.is-completed .tdog-engrave-progress__fill{
      background:var(--tdog-good);
    }
    .tdog-engrave-progress.is-not-started .tdog-engrave-progress__hint strong { color: rgba(0,0,0,.55); }
    .tdog-engrave-progress.is-in-progress .tdog-engrave-progress__hint strong { color: var(--tdog-info); }
    .tdog-engrave-progress.is-completed .tdog-engrave-progress__hint strong { color: var(--tdog-good); }

    .tdog-engrave-progress__text{
      font-weight:500;
      color:#4b5563;
    }
    .tdog-engrave-progress.is-completed .tdog-engrave-progress__text{
      color:var(--tdog-good);
    }

    .tdog-engrave-card{
      position: relative;
      margin: 12px 0;
      padding: 12px 14px;
      border-radius: 12px;
      border: 1px solid var(--tdog-muted);
      background: #F7F7F8;
      box-shadow: 0 1px 2px rgba(0,0,0,.04);
      overflow: hidden;
      transition: background-color .2s ease, border-color .2s ease, box-shadow .2s ease;
    }
    .tdog-engrave-card::before{
      content: "";
      position: absolute;
      top: 12px;
      bottom: 12px;
      left: 0;
      width: 4px;
      border-radius: 0 9999px 9999px 0;
      background: rgba(0,0,0,.18);
      pointer-events: none;
    }
    .tdog-engrave-card[data-state="not-started"]{
      background: #F7F7F8;
      border-color: rgba(0,0,0,.10);
    }
    .tdog-engrave-card[data-state="not-started"]::before{
      background: rgba(255, 238, 0, 1);
    }
    .tdog-engrave-card[data-state="in-progress"]{
      background: #FFF4EC;
      border-color: rgba(249,128,39,.20);
    }
    .tdog-engrave-card[data-state="in-progress"]::before{
      background: var(--tdog-warn);
    }
    .tdog-engrave-card[data-state="complete"],
    .tdog-engrave-card[data-state="skipped"]{
      background: #EDF9F9;
      border-color: rgba(5,173,174,.20);
    }
    .tdog-engrave-card[data-state="complete"]::before,
    .tdog-engrave-card[data-state="skipped"]::before{
      background: var(--tdog-good);
    }
    .tdog-engrave-card .cl-po--panel{
      padding-top: 8px;
    }
    .cl-po--collapse.tdog-engrave-collapse{
      display:flex;
      align-items:center;
      gap:12px;
      width:100%;
      flex-wrap:nowrap;
    }
    .cl-po--collapse.tdog-engrave-collapse::before{
      content:"";
      display:block;
      width:16px;
      min-width:16px;
      max-width:16px;
      height:16px;
      min-height:16px;
      max-height:16px;
      aspect-ratio:1 / 1;
      border-radius:50%;
      flex:0 0 16px;
      align-self:center;
      background: rgba(0,0,0,.18);
    }

    .tdog-engrave-badge{
      margin-left:auto;
      font-size:12px;
      font-weight:800;
      padding:4px 10px;
      border-radius:9999px;
      border:1px solid var(--tdog-muted);
      background: rgba(255,255,255,.7);
      color: rgba(0,0,0,.70);
      white-space:nowrap;
    }

    .cl-po--collapse.tdog-engrave-collapse[data-state="not-started"]::before{ background: rgba(255, 238, 0, 1); }
    .cl-po--collapse.tdog-engrave-collapse[data-state="in-progress"]::before{ background: var(--tdog-warn); }
    .cl-po--collapse.tdog-engrave-collapse[data-state="complete"]::before{ background: var(--tdog-good); }
    .cl-po--collapse.tdog-engrave-collapse[data-state="skipped"]::before{ background: #64748b; }

    .tdog-engrave-badge[data-state="not-started"]{ color: rgba(0,0,0,.65); }
    .tdog-engrave-badge[data-state="in-progress"]{ border-color: rgba(180,83,9,.35); color: var(--tdog-warn); }
    .tdog-engrave-badge[data-state="complete"]{ border-color: rgba(22,101,52,.35); color: var(--tdog-good); }
    .tdog-engrave-badge[data-state="skipped"]{
      border-color: rgba(100,116,139,.35);
      color: #64748b;
    }

    @media (max-width: 767px){
      .tdog-engrave-card{
        padding: 10px 12px;
        margin: 10px 0;
      }
      .cl-po--collapse.tdog-engrave-collapse{
        gap:10px;
      }
      .tdog-engrave-badge{
        flex-shrink:0;
      }
    }


    .tdog-engrave-row{
      display:flex;
      gap:10px;
      align-items:flex-start;
      margin: 10px 0 14px;
    }
    .tdog-engrave-row input[type="checkbox"]{ margin-top:2px; }

    .cl-po--font-selector .cl-po--swatch-container{
      display:grid!important;
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:10px!important;
    }
    @media (min-width:720px){
      .cl-po--font-selector .cl-po--swatch-container{
        grid-template-columns:repeat(3,minmax(0,1fr))!important;
      }
    }
    .cl-po--font-selector .cl-po--swatch-item.grow{margin:0!important}
    .cl-po--font-selector .cl-po--swatch.cl-po--swatch-img.cl-po--large{width:100%!important;border-radius:12px!important;overflow:hidden!important}

    .cl-po--font-selector img{width:100%!important;height:auto!important;display:block!important}
    .cl-po--font-selector .cl-po--swatch-container .cl-po--break{
      display:none !important;
    }

    .cl-po--font-selector .cl-po--swatch-container{
      grid-auto-flow: row dense !important;
    }
    .cl-po--font-selector .cl-po--swatch-item{
      min-width: 0 !important;
    }
  `;

  function ensureStyle() {
    if (document.getElementById(CONFIG.styleId)) return;

    const tag = document.createElement("style");
    tag.id = CONFIG.styleId;
    tag.textContent = CSS;
    document.head.appendChild(tag);
    log("CSS injected");
  }

  /************************************************************
   * New badge configuration
   ************************************************************/
  const DEFAULTS = Object.freeze({
    enabled: true,
    days: 30,
    tags: ["new", "new-arrival", "new_in", "just-in", "fresh"]
  });

  function readNewBadgeConfig() {
    const config = {
      enabled: DEFAULTS.enabled,
      days: DEFAULTS.days,
      tags: [...DEFAULTS.tags]
    };

    const nodes = safeRun(
      "readNewBadgeConfig.query",
      () => Array.from(document.querySelectorAll(SELECTORS.newBadgeConfig)),
      []
    );

    for (const node of nodes) {
      try {
        const data = JSON.parse(node.textContent || "{}");

        if (typeof data.enabled === "boolean") {
          config.enabled = data.enabled;
        }

        if (Number.isFinite(data.days)) {
          config.days = data.days;
        }

        if (Array.isArray(data.tags) && data.tags.length > 0) {
          const merged = config.tags.concat(
            data.tags.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean)
          );
          config.tags = Array.from(new Set(merged));
        }
      } catch (err) {
        warn("Invalid new badge config block ignored", err);
      }
    }

    return config;
  }

  const NEW_BADGE_CONFIG = readNewBadgeConfig();

  /************************************************************
   * Utilities
   ************************************************************/
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const INLINE_IMPORTANT_STYLES = Object.freeze({
    "border-radius": "9999px",
    "padding": "16px 28px",
    "min-height": "56px",
    "font-size": "16px",
    "font-weight": "600",
    "line-height": "1.4",
    "color": "#ffffff",
    "display": "inline-flex",
    "align-items": "center",
    "justify-content": "center",
    "background-color": "#de6ac1",
    "box-shadow": "0 1px 3px rgba(0,0,0,0.2)",
    "border": "none",
    "width": "100%",
    "cursor": "pointer",
    "text-align": "center",
    "margin": "0 auto"
  });

  const qsa = (root, selector) => {
    const scope = root || document;
    const results = Array.from(scope.querySelectorAll(selector));

    if (scope instanceof Element && scope.matches(selector)) {
      results.unshift(scope);
    }

    return results;
  };

  function debounce(fn, delay = 80) {
    let timerId = 0;
    return (...args) => {
      window.clearTimeout(timerId);
      timerId = window.setTimeout(() => fn(...args), delay);
    };
  }

  function cssEscape(str) {
    return (window.CSS && typeof window.CSS.escape === "function")
      ? window.CSS.escape(str)
      : String(str).replace(/[^a-zA-Z0-9_\-]/g, (char) => `\\${char}`);
  }

  function parseUnixSeconds(value) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
  }

  function moneyToCents(moneyStr) {
    if (!moneyStr) return null;

    const cleaned = String(moneyStr).replace(/[^\d.,-]/g, "").trim();
    if (!cleaned) return null;

    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    const normalized = lastComma > lastDot
      ? cleaned.replace(/\./g, "").replace(",", ".")
      : cleaned.replace(/,/g, "");

    const value = Number(normalized);
    return Number.isNaN(value) ? null : Math.round(value * 100);
  }

  function centsToMoney(cents) {
    if (typeof cents !== "number") return "";

    try {
      const currency = window.Shopify?.currency?.active || window.Shopify?.currency || "USD";
      const locale = window.Shopify?.locale || navigator.language || "en-US";

      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (err) {
      warn("centsToMoney formatting failed", err);
      return String((cents / 100).toFixed(2));
    }
  }

  function applyImportantStyles(element, styles) {
    for (const [property, value] of Object.entries(styles)) {
      element.style.setProperty(property, value, "important");
    }
  }

  function isElement(value) {
    return value instanceof Element;
  }

  function createElement(tagName, options = {}) {
    const element = document.createElement(tagName);

    if (options.className) {
      element.className = options.className;
    }

    if (options.text != null) {
      element.textContent = options.text;
    }

    if (options.attributes) {
      for (const [name, value] of Object.entries(options.attributes)) {
        if (value != null) {
          element.setAttribute(name, String(value));
        }
      }
    }

    return element;
  }

  function appendTextWithStrong(container, strongText, trailingText) {
    container.replaceChildren();

    const strong = document.createElement("strong");
    strong.textContent = strongText;

    container.appendChild(strong);
    container.appendChild(document.createTextNode(` ${trailingText}`));
  }

  function getRelevantRootsFromMutations(mutations) {
    const roots = new Set();

    for (const mutation of mutations) {
      const target = mutation.target;
      if (isElement(target)) {
        roots.add(target);
      }

      for (const node of mutation.addedNodes) {
        if (isElement(node)) {
          roots.add(node);
        }
      }
    }

    return roots.size > 0 ? Array.from(roots) : [document];
  }

  function elementMatchesOrContains(root, selector) {
    if (!isElement(root)) return false;
    return root.matches(selector) || !!root.querySelector(selector);
  }

  /************************************************************
   * Feature A: Personalise button styling
   ************************************************************/
  const processedButtons = new WeakSet();

  function stylePersonaliseButtons(root = document) {
    safeRun("stylePersonaliseButtons", () => {
      const buttons = qsa(root, SELECTORS.personaliseButton);
      let styledCount = 0;

      for (const button of buttons) {
        if (processedButtons.has(button)) {
          continue;
        }

        button.classList.add("tdog-personalise");
        applyImportantStyles(button, INLINE_IMPORTANT_STYLES);

        processedButtons.add(button);
        styledCount += 1;
      }

      if (styledCount > 0) {
        log(`Styled ${styledCount} personalise button(s)`);
      }
    });
  }

  /************************************************************
   * Feature B: NEW badge logic
   ************************************************************/
  const processedCards = new WeakSet();

  function hasNewTag(csv, tags) {
    if (!csv) return false;

    const normalized = String(csv).toLowerCase();
    return tags.some((tag) => normalized.includes(tag));
  }

  function isNew(card) {
    return safeRun("isNew", () => {
      if (!NEW_BADGE_CONFIG.enabled) return false;

      const tagsCsv = card?.dataset?.productTags ?? "";
      if (hasNewTag(tagsCsv, NEW_BADGE_CONFIG.tags)) {
        return true;
      }

      const created = parseUnixSeconds(card?.dataset?.productCreated);
      const published = parseUnixSeconds(card?.dataset?.productPublished);
      const timestamp = Math.max(created, published);

      if (!timestamp) return false;

      const ageDays = (Date.now() - (timestamp * 1000)) / MS_PER_DAY;
      return ageDays >= 0 && ageDays <= NEW_BADGE_CONFIG.days;
    }, false);
  }

  function createNewBadge() {
    return createElement("span", {
      className: "badge badge--new flex items-center font-medium leading-none rounded-full",
      text: "New",
      attributes: { "aria-label": "New product" }
    });
  }

  function placeNewBadges(root = document) {
    if (!NEW_BADGE_CONFIG.enabled) return;

    safeRun("placeNewBadges", () => {
      const cards = qsa(root, SELECTORS.productCard);
      let placedCount = 0;

      for (const card of cards) {
        if (processedCards.has(card)) {
          continue;
        }

        if (!isNew(card)) {
          processedCards.add(card);
          continue;
        }

        const media = card.querySelector(SELECTORS.productCardMedia);
        if (!media) {
          processedCards.add(card);
          continue;
        }

        if (!media.querySelector(".badge--new")) {
          media.appendChild(createNewBadge());
          placedCount += 1;
        }

        processedCards.add(card);
      }

      if (placedCount > 0) {
        log(`Placed ${placedCount} NEW badge(s)`);
      }
    });
  }

  /************************************************************
   * Feature C: Rapi Bundles
   ************************************************************/
  const boundRapiSections = new WeakSet();
  const rapiObserverByRoot = new WeakMap();
  const defaultButtonLabelBySection = new WeakMap();

  function hasRapiBundle(root = document) {
    const found = !!root.querySelector(SELECTORS.rapiRoot);
    log("hasRapiBundle?", found);
    return found;
  }

  function waitForRapi({ timeoutMs = CONFIG.waitForRapiTimeoutMs, pollMs = CONFIG.waitForRapiPollMs } = {}) {
    return new Promise((resolve) => {
      if (hasRapiBundle()) {
        resolve(true);
        return;
      }

      let resolved = false;
      const startTime = Date.now();

      const finish = (value) => {
        if (resolved) return;
        resolved = true;
        observer.disconnect();
        window.clearInterval(intervalId);
        resolve(value);
      };

      const observer = new MutationObserver(() => {
        if (hasRapiBundle()) {
          log("Rapi detected via MutationObserver");
          finish(true);
        }
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });

      const intervalId = window.setInterval(() => {
        if (hasRapiBundle()) {
          log("Rapi detected via polling");
          finish(true);
          return;
        }

        if ((Date.now() - startTime) > timeoutMs) {
          warn("Timed out waiting for Rapi");
          finish(false);
        }
      }, pollMs);
    });
  }

  function getFormAndId(sectionEl) {
    const form = sectionEl.querySelector(SELECTORS.productForm);
    const formId = form?.getAttribute("id") || null;
    return { form, formId };
  }

  function getBuyButtonByFormId(formId) {
    if (!formId) return null;
    return document.querySelector(SELECTORS.buyButtonByFormId(formId));
  }

  function getBuyPriceElByFormId(formId) {
    if (!formId) return null;

    const container = document.querySelector(SELECTORS.buyPriceContainerByFormId(formId));
    return container?.querySelector("product-buy-price.price")
      || getBuyButtonByFormId(formId)?.querySelector("product-buy-price.price")
      || null;
  }

  function getBuyLabelElByFormId(formId) {
    return getBuyButtonByFormId(formId)?.querySelector(SELECTORS.buyButtonLabel) || null;
  }

  function getRapiScope(sectionEl) {
    if (sectionEl?.querySelector(".rapi-bundles, [class*='rapi-bundle-content']")) {
      return sectionEl;
    }

    const nearestProduct = sectionEl?.closest?.("product-info, .product, .product-main, main") || null;
    if (nearestProduct?.querySelector(".rapi-bundles, [class*='rapi-bundle-content']")) {
      return nearestProduct;
    }

    return document;
  }

  function getCheckedBundleRadio(sectionEl) {
    const scope = getRapiScope(sectionEl);
    const radios = Array.from(scope.querySelectorAll(".rapi-bundles__bar input[type='radio']"));
    return radios.find((radio) => radio.checked) || null;
  }

  function isVisible(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
  }

  function getSelectedBundleContainer(sectionEl) {
    const scope = getRapiScope(sectionEl);
    const containers = Array.from(scope.querySelectorAll(".rapi-bundles__bar-container"));
    if (containers.length === 0) {
      return null;
    }

    const checkedRadio = getCheckedBundleRadio(sectionEl);
    if (checkedRadio?.id) {
      const checkedLabel = scope.querySelector(`label[for="${cssEscape(checkedRadio.id)}"]`);
      if (checkedLabel) {
        log("Resolved selected Rapi container via checked radio ->", checkedRadio.id);
        return checkedLabel;
      }
    }

    let bestContainer = null;
    let bestScore = -1;

    for (const container of containers) {
      let score = 0;
      const forId = container.getAttribute("for");
      const linkedRadio = forId ? scope.querySelector(`#${cssEscape(forId)}`) : null;
      const parentBar = container.closest(".rapi-bundles__bar");

      if (linkedRadio?.checked) {
        score += 1000;
      }

      const visibleExpandedBlocks = Array.from(container.querySelectorAll(".rapi--bb-royal-container, .rapi-content-inner-wrapper, .rapi-content-outer-wrapper"))
        .filter((el) => isVisible(el) && /add another|offer-image-preview|rapi--bb-royal/i.test(`${el.className} ${el.textContent || ""}`));
      if (visibleExpandedBlocks.length > 0) {
        score += 300;
      }

      if (container.querySelector(".rapi-pressable-button")) {
        score += 100;
      }

      const style = `${container.getAttribute("style") || ""} ${parentBar?.getAttribute("style") || ""}`.toLowerCase();
      if (style.includes("--bar-highlight-size")) {
        score += 50;
      }
      if (style.includes("box-shadow") && style.includes("inset")) {
        score += 40;
      }
      if (style.includes("--bar-selected-border-color") || style.includes("--bar-border-color: #05adaeff;")) {
        score += 20;
      }

      const radioVisual = container.querySelector(".rapi-bundles__bar-radio");
      if (radioVisual) {
        const rvStyle = `${radioVisual.getAttribute("style") || ""} ${window.getComputedStyle(radioVisual).cssText || ""}`.toLowerCase();
        if (rvStyle.includes("background") || rvStyle.includes("border")) {
          score += 5;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestContainer = container;
      }
    }

    if (bestContainer && bestScore > 0) {
      log("Resolved selected Rapi container via heuristic fallback ->", {
        forId: bestContainer.getAttribute("for"),
        score: bestScore
      });
      return bestContainer;
    }

    return null;
  }

  function getBundleLabelForRadio(sectionEl, radio) {
    if (!radio?.id) return null;
    return getRapiScope(sectionEl).querySelector(`label[for="${cssEscape(radio.id)}"]`);
  }

  function getSelectedBundleTitle(sectionEl) {
    const label = getSelectedBundleContainer(sectionEl);
    return label?.querySelector(SELECTORS.bundleTitle)?.textContent?.trim() || null;
  }

  function extractMoneyStrings(text) {
    if (!text) return [];
    const matches = String(text).match(/[$€£]\s*\d[\d,]*(?:\.\d{1,2})?|\d[\d,]*(?:\.\d{1,2})?\s*[$€£]/g);
    return matches || [];
  }

  function getSelectedBundlePriceCents(sectionEl) {
    const label = getSelectedBundleContainer(sectionEl);
    if (!label) {
      log("Skipped bundle price lookup: selected bundle container not found");
      return null;
    }

    const directPriceEl = label.querySelector(SELECTORS.bundlePrice);
    const directPriceText = directPriceEl?.textContent?.trim() || "";
    const directCents = moneyToCents(directPriceText);
    if (directCents != null) {
      return directCents;
    }

    const candidateSelectors = [
      "[data-price]",
      "product-buy-price",
      ".price",
      ".money",
      "[class*='price']",
      "[class*='Price']"
    ];

    for (const selector of candidateSelectors) {
      const candidates = Array.from(label.querySelectorAll(selector));
      for (const candidate of candidates) {
        if (candidate.closest('s, del, .line-through, [style*="line-through"]')) {
          continue;
        }

        const dataPrice = candidate.getAttribute?.("data-price");
        const centsFromData = dataPrice && /^\d+$/.test(dataPrice) ? Number(dataPrice) : null;
        if (centsFromData != null) {
          return centsFromData;
        }

        const centsFromText = moneyToCents(candidate.textContent || "");
        if (centsFromText != null) {
          return centsFromText;
        }
      }
    }

    const visibleMoneyMatches = extractMoneyStrings(label.textContent || "");
    for (const moneyText of visibleMoneyMatches) {
      const cents = moneyToCents(moneyText);
      if (cents != null) {
        log("Resolved bundle price from label text fallback ->", moneyText, cents);
        return cents;
      }
    }

    log("Skipped bundle price lookup: no parseable bundle price found");
    return null;
  }

  function ensureDefaultLabelCached(sectionEl) {
    if (defaultButtonLabelBySection.has(sectionEl)) {
      return;
    }

    const { formId } = getFormAndId(sectionEl);
    const labelEl = getBuyLabelElByFormId(formId);

    if (labelEl) {
      defaultButtonLabelBySection.set(sectionEl, labelEl.textContent.trim());
    }
  }

  function updateBuyButtonPrice(sectionEl) {
    const { formId } = getFormAndId(sectionEl);
    if (!formId) {
      log("Skipped Buy Button Price update: formId not found");
      return false;
    }

    const cents = getSelectedBundlePriceCents(sectionEl);
    if (cents == null) {
      log("Skipped Buy Button Price update: bundle price not available yet");
      return false;
    }

    const priceEl = getBuyPriceElByFormId(formId);
    if (!priceEl) {
      log("Skipped Buy Button Price update: buy price element not available yet for", formId);
      return false;
    }

    const formattedPrice = centsToMoney(cents);
    priceEl.setAttribute("data-price", String(cents));
    priceEl.textContent = formattedPrice;
    log("Updated Buy Button Price ->", formattedPrice);
    return true;
  }

  function updateBuyButtonLabel(sectionEl) {
    ensureDefaultLabelCached(sectionEl);

    const { formId } = getFormAndId(sectionEl);
    const labelEl = getBuyLabelElByFormId(formId);
    if (!labelEl) {
      log("Skipped Buy Button Label update: label element not available yet for", formId);
      return false;
    }

    const bundleTitle = getSelectedBundleTitle(sectionEl);
    const baseLabel = defaultButtonLabelBySection.get(sectionEl) || "Add to cart";

    if (bundleTitle) {
      const hasBundleWord = /bundle/i.test(bundleTitle);
      labelEl.textContent = `Add ${bundleTitle}${hasBundleWord ? "" : " bundle"} to cart`;
    } else {
      labelEl.textContent = baseLabel;
    }

    log("Updated Buy Button Label ->", labelEl.textContent);
    return true;
  }

  function syncRapiUI(sectionEl) {
    return safeRun("syncRapiUI", () => {
      const priceUpdated = updateBuyButtonPrice(sectionEl);
      const labelUpdated = updateBuyButtonLabel(sectionEl);
      return { priceUpdated: !!priceUpdated, labelUpdated: !!labelUpdated };
    }, { priceUpdated: false, labelUpdated: false });
  }

  function scheduleRapiSyncRetries(sectionEl, delays = [150, 400, 900, 1600]) {
    for (const delayMs of delays) {
      window.setTimeout(() => {
        const result = syncRapiUI(sectionEl);
        if (!result?.priceUpdated || !result?.labelUpdated) {
          log(`Rapi retry sync @ ${delayMs}ms`, result);
        }
      }, delayMs);
    }
  }

  function bindRapiSection(sectionEl) {
    if (!sectionEl || boundRapiSections.has(sectionEl)) {
      return;
    }

    boundRapiSections.add(sectionEl);

    const triggerUpdate = debounce(
      () => syncRapiUI(sectionEl),
      CONFIG.rapiSyncDebounceMs
    );

    sectionEl.addEventListener("change", (event) => {
      const target = event.target;
      if (!isElement(target)) return;

      if (
        target.matches(SELECTORS.checkedBundleRadio.replace(":checked", "")) ||
        target.matches(SELECTORS.bundleVariantSelect) ||
        !!target.closest(SELECTORS.bundleVariantSelect)
      ) {
        triggerUpdate();
      }
    });

    sectionEl.addEventListener("click", (event) => {
      const target = event.target;
      if (!isElement(target)) return;

      if (
        target.closest(".rapi-bundles__bar-container") ||
        target.closest(".rapi-bundles__bar") ||
        target.closest(".rapi-pressable-button")
      ) {
        triggerUpdate();
        window.setTimeout(() => syncRapiUI(sectionEl), 0);
        window.setTimeout(() => syncRapiUI(sectionEl), 120);
        window.setTimeout(() => syncRapiUI(sectionEl), 300);
      }
    }, true);

    const rapiRoot = sectionEl.querySelector(SELECTORS.rapiRoot);
    if (rapiRoot && !rapiObserverByRoot.has(rapiRoot)) {
      const observer = new MutationObserver(triggerUpdate);
      observer.observe(rapiRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      });
      rapiObserverByRoot.set(rapiRoot, observer);
    }

    triggerUpdate();
    scheduleRapiSyncRetries(sectionEl);
  }

  function bindAllRapiSections(root = document) {
    const sections = qsa(root, SELECTORS.productFormWrapper);
    if (sections.length > 0) {
      log(`Found ${sections.length} product-form-wrapper section(s) to bind`);
    }

    for (const section of sections) {
      bindRapiSection(section);
    }
  }

  /************************************************************
   * Feature D: Live Product Options (Engraving bundle UX)
   ************************************************************/
  const EngraveUX = (() => {
    const CLASSES = Object.freeze({
      noEngrave: "tdog-no-engrave",
      card: "tdog-engrave-card",
      badge: "tdog-engrave-badge",
      collapse: "tdog-engrave-collapse",
      progress: "tdog-engrave-progress",
      count: "tdog-engrave-progress__count",
      fill: "tdog-engrave-progress__fill",
      hint: "tdog-engrave-progress__hint"
    });

    const processedGroups = new WeakSet();
    const boundWrappers = new WeakSet();
    const engraveObserverByWrapper = new WeakMap();
    let generatedIdCounter = 0;

    function nextGeneratedId(prefix) {
      generatedIdCounter += 1;
      return `${prefix}-${generatedIdCounter}`;
    }

    function getMainProductForm(root = document) {
      return root.querySelector('form[action*="/cart/add"]')
        || root.querySelector("product-form form")
        || root.querySelector("form.product-form")
        || root.querySelector("form");
    }

    function getWrapperScoped(root = document) {
      const form = getMainProductForm(root);

      if (!form) {
        return root.querySelector(SELECTORS.engraveWrapper) || document.querySelector(SELECTORS.engraveWrapper);
      }

      return form.closest("section, .shopify-section, .product")?.querySelector(SELECTORS.engraveWrapper)
        || form.parentElement?.querySelector(SELECTORS.engraveWrapper)
        || root.querySelector(SELECTORS.engraveWrapper)
        || document.querySelector(SELECTORS.engraveWrapper);
    }

    function getGroups(wrapper) {
      if (!wrapper) return [];

      return qsa(wrapper, SELECTORS.engraveGroup).filter((group) =>
        !!group.querySelector(SELECTORS.engraveCollapse) && !!group.querySelector(SELECTORS.engravePanel)
      );
    }

    function getCollapse(groupEl) {
      return groupEl.querySelector(SELECTORS.engraveCollapse);
    }

    function getPanel(groupEl) {
      return groupEl.querySelector(SELECTORS.engravePanel);
    }

    function findTextInput(groupEl) {
      return getPanel(groupEl)?.querySelector(SELECTORS.engraveTextInput) || null;
    }

    function getFontSelected(groupEl) {
      return !!groupEl.querySelector(SELECTORS.engraveFontSelected);
    }

    function getApproveCheckbox(groupEl) {
      return groupEl.querySelector(SELECTORS.engraveApproveCheckbox);
    }

    function getNoEngraveCheckbox(groupEl) {
      return groupEl.querySelector(`input.${CLASSES.noEngrave}`);
    }

    function deriveItemName(groupEl) {
      const raw = String(groupEl.getAttribute("data-option") || "").trim();
      return raw.replace(/\s+/g, " ").replace(/\s*Group\s*$/i, "").trim() || "Item";
    }

    function setCollapseTitle(collapseEl, groupEl) {
      if (!collapseEl || collapseEl.dataset.tdogTitle === "1") {
        return;
      }

      collapseEl.dataset.tdogTitle = "1";

      const newTitle = `${deriveItemName(groupEl)} engraving (optional)`;
      const childNodes = Array.from(collapseEl.childNodes);
      const hasElementChildren = childNodes.some((node) => node.nodeType === Node.ELEMENT_NODE);

      if (!hasElementChildren) {
        collapseEl.textContent = newTitle;
        return;
      }

      const firstTextNode = childNodes.find((node) => node.nodeType === Node.TEXT_NODE);
      if (firstTextNode) {
        firstTextNode.textContent = ` ${newTitle} `;
      } else {
        collapseEl.insertAdjacentText("afterbegin", newTitle);
      }
    }

    function ensureBadge(collapseEl) {
      if (!collapseEl) return null;

      collapseEl.classList.add(CLASSES.collapse);

      let badge = collapseEl.querySelector(`.${CLASSES.badge}`);
      if (badge) return badge;

      badge = createElement("span", {
        className: CLASSES.badge,
        text: "Not started"
      });
      badge.dataset.state = "not-started";
      collapseEl.appendChild(badge);

      return badge;
    }

    function setBadge(collapseEl, state) {
      const badge = ensureBadge(collapseEl);
      if (!badge) return;

      collapseEl.dataset.state = state;
      badge.dataset.state = state;

      const card = collapseEl.closest(`.${CLASSES.card}`);
      if (card) {
        card.dataset.state = state;
      }

      badge.textContent = state === "complete"
        ? "Complete"
        : state === "in-progress"
          ? "In progress"
          : state === "skipped"
            ? "No engraving"
            : "Not started";
    }

    function getGroupState(groupEl) {
      const noEngraveCheckbox = getNoEngraveCheckbox(groupEl);
      if (noEngraveCheckbox?.checked) {
        return "skipped";
      }

      const textInput = findTextInput(groupEl);
      const textValue = (textInput?.value || "").trim();
      const fontSelected = getFontSelected(groupEl);
      const approveCheckbox = getApproveCheckbox(groupEl);
      const approveChecked = approveCheckbox ? approveCheckbox.checked : true;

      const started = !!textValue || fontSelected || !!approveCheckbox?.checked;
      if (!started) {
        return "not-started";
      }

      const complete = !!textValue && fontSelected && approveChecked;
      return complete ? "complete" : "in-progress";
    }

    function createProgressElement() {
      const root = createElement("div", { className: CLASSES.progress });

      const top = createElement("div", { className: "tdog-engrave-progress__top" });
      const title = createElement("div", { className: "tdog-engrave-progress__title", text: "Engraving" });
      const count = createElement("div", { className: CLASSES.count, text: "0 / 0 completed" });

      top.append(title, count);

      const bar = createElement("div", { className: "tdog-engrave-progress__bar" });
      const fill = createElement("div", { className: CLASSES.fill });
      bar.appendChild(fill);

      const hint = createElement("div", { className: CLASSES.hint });
      hint.textContent = "This bundle has engraving options for multiple items. Expand each section to personalise, or tick “No engraving for this item”.";

      root.append(top, bar, hint);
      return root;
    }

    function ensureProgress(wrapper) {
      let progress = wrapper.querySelector(`.${CLASSES.progress}`);
      if (progress) return progress;

      progress = createProgressElement();

      const firstGroup = wrapper.querySelector(SELECTORS.engraveGroup);
      (firstGroup || wrapper).insertAdjacentElement(firstGroup ? "beforebegin" : "afterbegin", progress);

      return progress;
    }

    function updateProgress(wrapper, groups) {
      const progress = ensureProgress(wrapper);
      const countEl = progress.querySelector(`.${CLASSES.count}`);
      const fillEl = progress.querySelector(`.${CLASSES.fill}`);
      const hintEl = progress.querySelector(`.${CLASSES.hint}`);
      if (!countEl || !fillEl || !hintEl) return;

      const total = groups.length;
      const completed = groups.reduce((count, group) => {
        const state = getGroupState(group);
        return count + ((state === "complete" || state === "skipped") ? 1 : 0);
      }, 0);

      const remaining = Math.max(total - completed, 0);
      const percentage = total > 0 ? (completed / total) * 100 : 0;

      let label = "Completed";
      let message = "All items confirmed. Your engraving selections are ready.";

      if (completed === 0) {
        label = "Not started";
        message = "Choose engraving or ‘No engraving’ for each item.";
      } else if (completed < total) {
        label = "In progress";
        message = `You still have ${remaining} item${remaining === 1 ? "" : "s"} to confirm.`;
      }

      countEl.textContent = `${completed} / ${total} completed`;
      fillEl.style.width = `${percentage}%`;
      fillEl.style.background = completed === 0
        ? "rgba(0,0,0,.22)"
        : completed < total
          ? "var(--tdog-warn)"
          : "var(--tdog-good)";

      appendTextWithStrong(hintEl, `${label}.`, message);

      progress.classList.remove("is-not-started", "is-in-progress", "is-completed");
      progress.classList.add(
        completed === 0
          ? "is-not-started"
          : completed < total
            ? "is-in-progress"
            : "is-completed"
      );
    }

    function dispatchInputAndChange(element) {
      element.dispatchEvent(new Event("input", { bubbles: true }));
      element.dispatchEvent(new Event("change", { bubbles: true }));
    }

    function ensureNoEngraveRow(groupEl, wrapper) {
      const panel = getPanel(groupEl);
      if (!panel) return;

      if (panel.querySelector(`input.${CLASSES.noEngrave}`)) {
        return;
      }

      const textInput = findTextInput(groupEl);
      if (!textInput) {
        return;
      }

      const checkboxId = nextGeneratedId("tdog-no");
      const row = createElement("div", { className: "tdog-engrave-row" });
      const checkbox = createElement("input", {
        className: CLASSES.noEngrave,
        attributes: {
          type: "checkbox",
          id: checkboxId
        }
      });
      const label = createElement("label", {
        text: "No engraving for this item",
        attributes: { for: checkboxId }
      });

      checkbox.addEventListener("change", () => {
        if (checkbox.checked) {
          textInput.value = "";
          dispatchInputAndChange(textInput);
        }

        updateEngraveUI(wrapper);
      });

      textInput.addEventListener("input", () => {
        if ((textInput.value || "").trim().length > 0 && checkbox.checked) {
          checkbox.checked = false;
        }
      });

      row.append(checkbox, label);
      textInput.insertAdjacentElement("afterend", row);
    }

    function updateEngraveUI(wrapper) {
      if (!wrapper) return;

      const groups = getGroups(wrapper);
      if (groups.length < 2) return;

      ensureProgress(wrapper);

      for (const group of groups) {
        const collapse = getCollapse(group);
        if (!collapse) continue;

        group.classList.add(CLASSES.card);
        collapse.classList.add(CLASSES.collapse);
        setCollapseTitle(collapse, group);
        setBadge(collapse, getGroupState(group));
      }

      updateProgress(wrapper, groups);
    }

    function bindDelegatedListeners(wrapper) {
      if (!wrapper || boundWrappers.has(wrapper)) {
        return;
      }

      boundWrappers.add(wrapper);

      const debouncedUpdate = debounce(
        () => updateEngraveUI(wrapper),
        CONFIG.engraveSyncDebounceMs
      );

      wrapper.addEventListener("input", debouncedUpdate, true);
      wrapper.addEventListener("change", debouncedUpdate, true);

      if (!engraveObserverByWrapper.has(wrapper)) {
        const observer = new MutationObserver((mutations) => {
          const relevant = mutations.some((mutation) =>
            Array.from(mutation.addedNodes).some((node) => isElement(node))
            || mutation.type !== "childList"
          );

          if (relevant) {
            debouncedUpdate();
          }
        });

        observer.observe(wrapper, {
          childList: true,
          subtree: true,
          attributes: true,
          characterData: false
        });

        engraveObserverByWrapper.set(wrapper, observer);
      }
    }

    function enhance(root = document) {
      safeRun("EngraveUX.enhance", () => {
        const wrapper = getWrapperScoped(root);
        if (!wrapper) return;

        const groups = getGroups(wrapper);
        if (groups.length < 2) return;

        for (const group of groups) {
          if (!processedGroups.has(group)) {
            processedGroups.add(group);
            ensureNoEngraveRow(group, wrapper);
          }

          const collapse = getCollapse(group);
          if (collapse) {
            ensureBadge(collapse);
            setCollapseTitle(collapse, group);
          }
        }

        bindDelegatedListeners(wrapper);
        updateEngraveUI(wrapper);
      });
    }

    return {
      enhance,
      updateEngraveUI
    };
  })();

  function enhanceEngravingBundleUX(root = document) {
    EngraveUX.enhance(root);
  }

  /************************************************************
   * Boot + dynamic hooks
   ************************************************************/
  function processStatic(root = document) {
    safeRun("processStatic", () => {
      ensureStyle();
      stylePersonaliseButtons(root);
      placeNewBadges(root);
      enhanceEngravingBundleUX(root);
    });
  }

  function processDynamicRoots(roots) {
    for (const root of roots) {
      processStatic(root);

      if (elementMatchesOrContains(root, SELECTORS.rapiRoot) || elementMatchesOrContains(root, SELECTORS.productFormWrapper)) {
        bindAllRapiSections(root);
      }
    }
  }

  async function boot() {
    processStatic(document);

    const globalObserver = new MutationObserver(
      debounce((mutations) => {
        processDynamicRoots(getRelevantRootsFromMutations(mutations));
      }, CONFIG.globalObserverDebounceMs)
    );

    globalObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    const scheduleStartupRescan = (delayMs) => {
      window.setTimeout(() => {
        log(`Startup rescan @ ${delayMs}ms`);
        processStatic(document);
        bindAllRapiSections(document);
      }, delayMs);
    };

    bindAllRapiSections(document);
    scheduleStartupRescan(250);
    scheduleStartupRescan(750);
    scheduleStartupRescan(1500);

    const onShopifySectionEvent = (event) => {
      const root = isElement(event.target) ? event.target : document;
      log("Shopify section event", event.type, root);
      processStatic(root);
      bindAllRapiSections(root);
    };

    document.addEventListener("shopify:section:load", onShopifySectionEvent);
    document.addEventListener("shopify:section:select", onShopifySectionEvent);

    const detected = await waitForRapi();
    if (!detected) {
      warn("Rapi not detected; bundle sync will continue through the global observer.");
      return;
    }

    bindAllRapiSections(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      boot().catch((err) => error("Boot failed", err));
    }, { once: true });
  } else {
    boot().catch((err) => error("Boot failed", err));
  }
})();